import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Star, Heart, Smile, Trophy, Dumbbell, Instagram, ArrowRight, ShieldCheck, VolumeX, BookOpen, Timer, ChevronDown, ShoppingCart } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { useCartStore } from '../store/useCartStore';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.5,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-104">
    {children}
  </motion.div>;
export function LandingPage() {
  const navigate = useNavigate();
  const {
    addItem,
    items,
    openCart
  } = useCartStore();
  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBuyClick = (e: React.MouseEvent, product: (typeof PRODUCTS)[0]) => {
    e.stopPropagation();
    addItem(product);
  };
  return <div className="min-h-screen bg-brand-bg text-brand-text overflow-x-hidden selection:bg-brand-yellow selection:text-brand-blueDark pt-24" data-id="element-105">
      {/* БЛОК 1: Головний екран (Hero Section) */}
      <section className="relative pt-4 pb-16 lg:pt-8 lg:pb-20 overflow-hidden" data-id="element-106">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" data-id="element-107">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-108">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="text-center lg:text-left order-1" data-id="element-109">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] mb-5 text-brand-blue uppercase tracking-tight" data-id="element-110">
                Це не просто <br data-id="element-111" />
                <span className="text-[1.1em] text-brand-blue block mt-1" data-id="element-112">
                  Планер
                </span>
              </h1>

              <div className="hidden lg:inline-block bg-brand-blue text-white px-5 py-2.5 rounded-xl mb-8 transform -rotate-1" data-id="element-113">
                <p className="text-lg md:text-xl font-bold" data-id="element-114">
                  Це спосіб навчити дитину <br className="hidden md:block" data-id="element-115" />
                  <span className="text-brand-yellow" data-id="element-116">самостійності</span> без
                  криків
                </p>
              </div>

              <div className="hidden lg:flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-117">
                <button onClick={scrollToProducts} className="px-8 py-4 bg-brand-yellow hover:bg-brand-yellowHover text-brand-blueDark rounded-xl font-black text-lg shadow-button transition-all transform hover:scale-105 flex items-center justify-center gap-2" data-id="element-118">
                  Замовити планер
                  <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-119" />
                </button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="relative order-2 max-w-md mx-auto lg:max-w-full flex flex-col items-center" data-id="element-120">
              <div className="relative rounded-[2rem] overflow-hidden shadow-xl border-4 border-white bg-white transform rotate-1 hover:rotate-0 transition-transform duration-500 mb-6 w-full" data-id="element-121">
                <img src="https://cdn.magicpatterns.com/uploads/cdrFBjpV29uCPC4QsJUG8h/IMG_5206.png" alt="MyPlaner - Інтерактивний планер для дітей" className="w-full h-auto object-cover" data-id="element-122" />
              </div>

              <div className="lg:hidden bg-brand-blue text-white inline-block px-5 py-3 rounded-xl mb-6 transform -rotate-1 text-center w-full" data-id="element-123">
                <p className="text-lg font-bold" data-id="element-124">
                  Це спосіб навчити дитину <br data-id="element-125" />
                  <span className="text-brand-yellow" data-id="element-126">самостійності</span> без
                  криків
                </p>
              </div>

              <div className="flex lg:hidden flex-col sm:flex-row gap-4 w-full justify-center" data-id="element-127">
                <button onClick={scrollToProducts} className="w-full px-8 py-4 bg-brand-yellow hover:bg-brand-yellowHover text-brand-blueDark rounded-xl font-black text-lg shadow-button transition-all transform hover:scale-105 flex items-center justify-center gap-2" data-id="element-128">
                  Замовити планер
                  <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-129" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* БЛОК 2: Болі батьків (Проблема) */}
      <section className="py-16 bg-white relative" data-id="element-130">
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none transform -translate-y-full" data-id="element-131">
          <svg className="relative block w-full h-[24px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" data-id="element-132">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C59.71,118.08,130.83,119.3,192.39,101.5,238.13,88.4,281.94,71.56,321.39,56.44Z" fill="#ffffff" data-id="element-133"></path>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-134">
          <FadeIn data-id="element-135">
            <div className="text-center mb-12" data-id="element-136">
              <h2 className="text-3xl md:text-4xl font-black text-brand-blue mb-4" data-id="element-137">
                Знайомі ситуації? 😩
              </h2>
              <p className="text-lg text-brand-muted max-w-2xl mx-auto font-medium" data-id="element-138">
                Кожен день схожий на день бабака, а прості справи перетворюються
                на випробування.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-139">
            {[{
            icon: <VolumeX className="w-6 h-6 text-brand-blue" data-id="element-140" />,
            title: 'Нескінченні нагадування',
            desc: 'Втомилися щодня повторювати «почисть зуби», «прибери іграшки» або «заправ ліжко»?'
          }, {
            icon: <BookOpen className="w-6 h-6 text-brand-blue" data-id="element-141" />,
            title: 'Сварки через уроки',
            desc: 'Дитина сідає за домашнє завдання тільки після довгих вмовлянь та зіпсованого настрою?'
          }, {
            icon: <Timer className="w-6 h-6 text-brand-blue" data-id="element-142" />,
            title: 'Стресові ранки',
            desc: 'Ранкові збори в садок або школу перетворюються на хаос і забирають енергію на весь день?'
          }].map((item, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-143">
                <div className="bg-brand-bg rounded-2xl p-6 border border-gray-100 hover:border-brand-blue/20 transition-all h-full flex flex-col" data-id="element-144">
                  <div className="bg-white w-12 h-12 rounded-xl shadow-sm flex items-center justify-center mb-5" data-id="element-145">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-brand-blue" data-id="element-146">
                    {item.title}
                  </h3>
                  <p className="text-brand-muted text-base font-medium leading-relaxed" data-id="element-147">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК 3: Об'єднаний блок (Фото + Як працює + Купити) */}
      <section className="py-12 lg:py-20 bg-brand-bg" data-id="element-148">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-149">
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 overflow-hidden flex flex-col lg:grid lg:grid-cols-2" data-id="element-150">
            {/* Ліва частина: Фото планера (Тільки для ПК) */}
            <div className="hidden lg:block relative bg-brand-blue/5 overflow-hidden min-h-[500px]" data-id="element-151">
              <img src="https://cdn.magicpatterns.com/uploads/w3mah8GUReZcNXMHpqYgWg/IMG_5249.jpg" alt="Дівчинка заповнює планер" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-152" />
            </div>

            {/* Права частина: Опис, Мобільне фото, Ціна та кнопка */}
            <div className="p-6 lg:p-12 flex flex-col" data-id="element-153">
              <FadeIn data-id="element-154">
                <h2 className="text-3xl md:text-4xl font-black text-brand-blue mb-6" data-id="element-155">
                  Як працює MyPlaner?
                </h2>

                <div className="space-y-5 mb-8 lg:mb-10" data-id="element-156">
                  {[{
                  title: 'Формує корисні звички',
                  desc: 'Вмивання, прибирання, читання стають автоматичними діями.'
                }, {
                  title: 'Мотивує через гру та нагороди',
                  desc: 'Збери 7 зірочок за тиждень — отримай заслужений приз!'
                }, {
                  title: 'Зменшує стрес і нагадування',
                  desc: 'Більше жодних нагадувань. Дитина сама бачить свій план.'
                }, {
                  title: 'Зона Workout',
                  desc: 'Фізична активність щодня: присідання, віджимання, планка.'
                }].map((item, idx) => <div key={idx} className="flex gap-4" data-id="element-157">
                      <div className="flex-shrink-0 mt-1" data-id="element-158">
                        <CheckCircle2 className="w-6 h-6 text-brand-yellow fill-brand-yellow/20" data-id="element-159" />
                      </div>
                      <div data-id="element-160">
                        <h4 className="text-lg font-bold text-brand-blue" data-id="element-161">
                          {item.title}
                        </h4>
                        <p className="text-brand-muted font-medium text-sm" data-id="element-162">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                {/* Фото планера (Тільки для мобільних, між текстом і ціною) */}
                <div className="lg:hidden mb-8 bg-brand-blue/5 rounded-3xl p-4 flex items-center justify-center -mx-2" data-id="element-163">
                  <div className="relative rounded-2xl overflow-hidden shadow-lg border-4 border-white" data-id="element-164">
                    <img src="https://cdn.magicpatterns.com/uploads/w3mah8GUReZcNXMHpqYgWg/IMG_5249.jpg" alt="Дівчинка заповнює планер" className="w-full h-auto object-cover" data-id="element-165" />
                  </div>
                </div>

                <div className="bg-brand-bg rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-6 border border-gray-100" data-id="element-166">
                  <div className="text-center sm:text-left" data-id="element-167">
                    <p className="text-sm text-brand-muted font-bold uppercase tracking-wider mb-1" data-id="element-168">
                      Вартість планера
                    </p>
                    <div className="text-4xl font-black text-brand-blue" data-id="element-169">
                      1000 <span className="text-2xl" data-id="element-170">грн</span>
                    </div>
                  </div>
                  <button onClick={scrollToProducts} className="w-full sm:w-auto px-8 py-4 bg-brand-yellow hover:bg-brand-yellowHover text-brand-blueDark rounded-xl font-black text-lg shadow-button transition-all transform hover:scale-105 flex items-center justify-center gap-2" data-id="element-171">
                    Купити зараз
                    <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-172" />
                  </button>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* БЛОК ТОВАРІВ (Магазин) */}
      <section id="products" className="py-20 bg-white" data-id="element-173">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-174">
          <FadeIn data-id="element-175">
            <div className="text-center mb-16" data-id="element-176">
              <h2 className="text-4xl md:text-5xl font-black text-brand-blue mb-4" data-id="element-177">
                Оберіть свій дизайн 🎨
              </h2>
              <p className="text-lg text-brand-muted font-medium max-w-2xl mx-auto" data-id="element-178">
                У нас є ідеальний планер як для маленьких принцес, так і для
                майбутніх чемпіонів.
              </p>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-179">
            {PRODUCTS.map((product, idx) => <FadeIn key={product.id} delay={idx * 0.1} data-id="element-180">
                <div onClick={() => navigate(`/product/${product.id}`)} className="bg-brand-bg rounded-3xl overflow-hidden border border-gray-100 hover:shadow-card transition-all group flex flex-col h-full cursor-pointer transform hover:-translate-y-1" data-id="element-181">
                  <div className="relative aspect-[3/4] overflow-hidden bg-white" data-id="element-182">
                    <img src={product.img} alt={product.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" data-id="element-183" />
                  </div>
                  <div className="p-6 flex flex-col flex-grow" data-id="element-184">
                    <h3 className="text-xl font-black text-brand-blue mb-2" data-id="element-185">
                      {product.title}
                    </h3>
                    <p className="text-brand-muted text-sm font-medium mb-6 flex-grow" data-id="element-186">
                      {product.desc}
                    </p>

                    <div className="flex items-center justify-between mt-auto" data-id="element-187">
                      <div className="text-2xl font-black text-brand-blue" data-id="element-188">
                        {product.price} <span className="text-sm" data-id="element-189">грн</span>
                      </div>
                      <button onClick={e => handleBuyClick(e, product)} className="bg-brand-yellow hover:bg-brand-yellowHover text-brand-blueDark px-5 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2" data-id="element-190">
                        Купити
                      </button>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК FAQ: Питання та відповіді */}
      <section className="py-16 bg-brand-bg" data-id="element-191">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-192">
          <FadeIn data-id="element-193">
            <div className="text-center mb-12" data-id="element-194">
              <h2 className="text-3xl md:text-4xl font-black text-brand-blue mb-4" data-id="element-195">
                Часті запитання
              </h2>
            </div>
          </FadeIn>

          <div className="space-y-4" data-id="element-196">
            {[{
            q: 'Для якого віку підходить планер?',
            a: 'Планер ідеально підходить для дітей від 4 до 10 років. Малюки можуть орієнтуватися по картинках, а старші діти — читати завдання самостійно.'
          }, {
            q: 'Як він кріпиться?',
            a: 'Планер має повністю магнітну основу. Він надійно тримається на холодильнику або будь-якій іншій металевій поверхні і не сповзає.'
          }, {
            q: 'Чим писати на планері?',
            a: 'У комплекті йде спеціальний маркер з губкою. Дитина може легко ставити галочки та стирати їх наприкінці тижня. Поверхня планера багаторазова.'
          }].map((faq, idx) => {
            const [isOpen, setIsOpen] = useState(false);
            return <FadeIn key={idx} delay={idx * 0.1} data-id="element-197">
                  <div className="border border-gray-200 rounded-2xl overflow-hidden bg-brand-bg/50" data-id="element-198">
                    <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-199">
                      <span className="font-bold text-brand-blue text-lg" data-id="element-200">
                        {faq.q}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-brand-blue transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-201" />
                    </button>
                    <AnimatePresence data-id="element-202">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-203">
                          <div className="px-6 pb-5 text-brand-muted font-medium" data-id="element-204">
                            {faq.a}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </FadeIn>;
          })}
          </div>
        </div>
      </section>

      {/* БЛОК 5: Соціальний доказ та довіра */}
      <section className="py-16 bg-brand-bg" data-id="element-205">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-206">
          <FadeIn data-id="element-207">
            <div className="flex justify-center mb-8" data-id="element-208">
              <div className="inline-flex items-center gap-2 bg-brand-bg px-5 py-2 rounded-full border border-gray-200" data-id="element-209">
                <ShieldCheck className="w-5 h-5 text-brand-blue" data-id="element-210" />
                <span className="font-bold text-brand-blue text-sm" data-id="element-211">
                  Зроблено в Україні 🇺🇦 з любов'ю до дітей
                </span>
              </div>
            </div>

            <div className="text-center mb-12" data-id="element-212">
              <h2 className="text-3xl md:text-4xl font-black text-brand-blue mb-4" data-id="element-213">
                Батьки рекомендують ❤️
              </h2>
              <p className="text-lg text-brand-muted font-medium" data-id="element-214">
                Сотні сімей вже забули про крики вранці.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-215">
            {[{
            name: 'Анна, мама 6-річного Максима',
            text: 'Це просто порятунок! Раніше кожен ранок починався зі сліз та вмовлянь почистити зуби. Тепер син сам біжить до холодильника відмічати галочки. Чекає кінця тижня, щоб отримати свій приз.',
            img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }, {
            name: 'Олена, мама двійнят',
            text: "Купили відразу два планери. У дітей з'явився змагальний дух! Самі заправляють ліжко, роблять зарядку. Я нарешті можу спокійно випити каву вранці.",
            img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }, {
            name: 'Ігор, тато 8-річної Софії',
            text: "Відмінна річ для привчання до дисципліни без тиску. Донька сама обирає собі 'Завдання тижня' і дуже пишається, коли збирає всі зірочки. Якість магніту супер!",
            img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }].map((review, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-216">
                <div className="bg-brand-bg p-6 rounded-2xl h-full flex flex-col border border-gray-100" data-id="element-217">
                  <div className="flex gap-1 mb-4" data-id="element-218">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-brand-yellow fill-brand-yellow" data-id="element-219" />)}
                  </div>
                  <p className="text-brand-text font-medium text-base leading-relaxed mb-6 flex-grow" data-id="element-220">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-3" data-id="element-221">
                    <img src={review.img} alt={review.name} className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm" data-id="element-222" />
                    <p className="font-bold text-brand-blue text-sm" data-id="element-223">
                      {review.name}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК 6: Підвал (Footer) */}
      <footer className="bg-brand-blue text-white pt-16 pb-10 relative overflow-hidden" data-id="element-224">
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none transform -translate-y-full rotate-180" data-id="element-225">
          <svg className="relative block w-full h-[24px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" data-id="element-226">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C59.71,118.08,130.83,119.3,192.39,101.5,238.13,88.4,281.94,71.56,321.39,56.44Z" fill="#ffffff" data-id="element-227"></path>
          </svg>
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10" data-id="element-228">
          <FadeIn data-id="element-229">
            <h2 className="text-3xl md:text-4xl font-black mb-4" data-id="element-230">
              Готові до спокійних ранків?
            </h2>
            <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto font-medium" data-id="element-231">
              Замовте MyPlaner сьогодні та почніть формувати корисні звички
              вашої дитини вже завтра.
            </p>

            <button className="px-8 py-4 bg-brand-yellow hover:bg-brand-yellowHover text-brand-blueDark rounded-xl font-black text-xl shadow-button transition-all transform hover:scale-105 mb-12 inline-flex items-center gap-2" data-id="element-232">
              Купити за 1000 грн
              <ArrowRight className="w-6 h-6" strokeWidth={3} data-id="element-233" />
            </button>
          </FadeIn>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-234">
            <div className="text-2xl font-black tracking-tight flex items-center" data-id="element-235">
              <span className="text-brand-yellow font-script italic mr-1" data-id="element-236">
                My
              </span>
              <span className="text-white" data-id="element-237">Planer</span>
            </div>

            <div className="flex items-center gap-4" data-id="element-238">
              <a href="https://www.instagram.com/myplaner.ua/p/DXq4XE3jJA8/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-200 hover:text-white transition-colors" data-id="element-239">
                <Instagram className="w-5 h-5" data-id="element-240" />
                <span className="font-bold" data-id="element-241">@myplaner.ua</span>
              </a>
            </div>
          </div>

          <div className="mt-8 text-blue-300 text-sm flex flex-col md:flex-row justify-center gap-4 font-medium" data-id="element-242">
            <a href="#" className="hover:text-white transition-colors" data-id="element-243">
              Умови доставки та оплати
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-244">
              Політика конфіденційності
            </a>
            <span data-id="element-245">
              © {new Date().getFullYear()} MyPlaner. Всі права захищені.
            </span>
          </div>
        </div>
      </footer>
    </div>;
}